import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ProvinceSelect from "../ProvinceSelect";
import { api, getApiErrorMessage } from "../../api/client";
import { calendarDayString } from "../../lib/calendar-date";
import { clearablePatch, formValue } from "./rentalEdit";

/**
 * Edit a rental order (#815 PR F): PUT /surface-rentals/orders/:id.
 *
 * Absent keeps, a value sets, an emptied field is sent as null and CLEARS
 * (api#989). The API decides what may be cleared: start_date may not, and its
 * 400 FIELD_NOT_CLEARABLE is shown as sent. "Open-ended" clears
 * expected_return; the API re-checks the order's assets for overlap and a
 * clash is its 409 (ASSET_DOUBLE_BOOKED), shown as sent.
 */

const TEXT_FIELDS: { key: string; label: string; type?: string }[] = [
  { key: "well_name", label: "Well / site" },
  { key: "lsd", label: "LSD" },
  { key: "afe_number", label: "AFE" },
  { key: "po_number", label: "PO" },
  { key: "job_site_name", label: "Job site name" },
  { key: "job_site_address", label: "Job site address" },
  { key: "contact_name", label: "Site contact" },
  { key: "contact_phone", label: "Contact phone", type: "tel" },
  { key: "contact_email", label: "Contact email", type: "email" },
];

const formOf = (order: Record<string, unknown>): Record<string, string> => ({
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, formValue(order[f.key])])),
  start_date: calendarDayString(formValue(order.start_date)) ?? "",
  expected_return: calendarDayString(formValue(order.expected_return)) ?? "",
  province_state: formValue(order.province_state),
  notes: formValue(order.notes),
});

export default function RentalOrderEditDialog({
  order,
  country,
  onClose,
  onSaved,
}: {
  order: Record<string, unknown>;
  country?: "CA" | "US";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [before] = useState(() => formOf(order));
  const [form, setForm] = useState(before);
  const [openEnded, setOpenEnded] = useState(before.expected_return === "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setRefusal(null);
    const body = clearablePatch(before, { ...form, expected_return: openEnded ? "" : form.expected_return });
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.rentals.orders.update(String(order.id), body);
      toast.success("Order saved");
      onSaved();
      onClose();
    } catch (error) {
      // FIELD_NOT_CLEARABLE, ASSET_DOUBLE_BOOKED, a bad province: the API's words.
      setRefusal(getApiErrorMessage(error, "The order was not saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit order {formValue(order.order_number)}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="order-edit-form">
          <div className="min-w-0">
            <Label htmlFor="order-start">Start date</Label>
            <Input id="order-start" type="date" value={form.start_date} onChange={set("start_date")} />
          </div>
          <div className="min-w-0">
            <Label htmlFor="order-return">Expected return</Label>
            <Input id="order-return" type="date" value={openEnded ? "" : form.expected_return} disabled={openEnded} onChange={set("expected_return")} />
            <label className="mt-1 flex items-center gap-2 text-xs text-fieldiq-muted">
              <input type="checkbox" checked={openEnded} onChange={(e) => setOpenEnded(e.target.checked)} />
              Open-ended
            </label>
          </div>
          {TEXT_FIELDS.map((f) => (
            <div key={f.key} className="min-w-0">
              <Label htmlFor={`order-${f.key}`}>{f.label}</Label>
              <Input id={`order-${f.key}`} type={f.type ?? "text"} value={form[f.key]} onChange={set(f.key)} />
            </div>
          ))}
          <div className="min-w-0">
            <Label htmlFor="order-province">Province / state</Label>
            <ProvinceSelect
              id="order-province"
              country={country}
              value={form.province_state}
              onChange={(v) => setForm((f) => ({ ...f, province_state: v }))}
            />
          </div>
          <div className="min-w-0 sm:col-span-2">
            <Label htmlFor="order-notes">Notes</Label>
            <Textarea id="order-notes" value={form.notes} onChange={set("notes")} />
          </div>
        </div>
        <p className="text-xs text-fieldiq-muted">An emptied field is cleared.</p>
        {refusal && (
          <p role="alert" data-testid="order-edit-refusal" className="rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">
            {refusal}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
